// gltfImportChoice — WHAT KIND of glTF is this, asked rather than assumed.
//
// Three different assets arrive as `.glb`: a body animation, a character rig
// and a garment. `gltfKind.js` can tell them apart from the bytes, and it is
// right on every file measured — but it DECIDED, silently, and that is the part
// that was wrong. Its own garment branch is "unskinned meshes with geometry",
// which is every unskinned mesh in the world: a static prop, a piece of set
// dressing, a Marvelous garment. A confident wrong answer that nobody is shown
// is worse than a question.
//
// So the sniff becomes a SUGGESTION and the importer asks. Unreal's own model:
// dropping an FBX opens an import dialog with the detected settings filled in,
// and you confirm or change them. Nobody types the answer from scratch and
// nobody is silently overruled.
//
// Pure: no React, no three, no bytes. `node --test` drives it directly.

import { GLTF_KIND } from './gltfKind.js'

/**
 * The kinds a dropped glTF can be imported as, in the order the dropdown shows
 * them. `wizardKey` is the uploader the choice routes to — the SAME registry
 * key the extension dispatch uses, so a choice cannot name an uploader that
 * does not exist.
 */
export const GLTF_IMPORT_TYPES = Object.freeze([
  {
    kind: GLTF_KIND.garment,
    wizardKey: 'garment',
    label: 'Garment / clothing',
    hint: 'A draped mesh from Marvelous Designer. Skin weights are transferred from the character when it is worn.',
  },
  {
    kind: GLTF_KIND.animation,
    wizardKey: 'body-anim',
    label: 'Body animation',
    hint: 'An animation clip that plays on the character rig.',
  },
  {
    kind: GLTF_KIND.rig,
    wizardKey: 'rig-replace',
    label: 'Character rig (replaces this character\u2019s rig)',
    hint: 'Destructive: it swaps the rig the whole character is built on, and blueprints that name removed bones stop binding.',
    destructive: true,
  },
])

/** The wizard key for one import kind, or null. */
export function wizardKeyForKind(kind) {
  return GLTF_IMPORT_TYPES.find(t => t.kind === kind)?.wizardKey ?? null
}

/**
 * One sentence saying what the bytes look like, shown under the dropdown so the
 * suggestion is ACCOUNTABLE — the user can see what it was read off, and
 * disagree with it knowing why it guessed.
 *
 * `unknown` is not a failure here. It is the honest state for a glTF whose
 * shape matches none of the three signatures, and the dialog still lets the
 * user import it as whatever they know it is.
 */
export function describeDetection(detected) {
  if (!detected || !detected.kind || detected.kind === GLTF_KIND.unknown) {
    return detected?.reason
      ? `This file matches none of the known shapes (${detected.reason}). Pick the type yourself.`
      : 'This file could not be read as a glTF. Pick the type yourself.'
  }
  const label = GLTF_IMPORT_TYPES.find(t => t.kind === detected.kind)?.label ?? detected.kind
  return `Looks like a ${label.toLowerCase()} \u2014 ${detected.reason}.`
}

/**
 * Everything the import dialog needs for one dropped file.
 *
 * `suggested` is the PRE-SELECTION, never the decision: it is null for an
 * unrecognised shape, which leaves the dialog with nothing chosen and its
 * confirm button disabled, so an unreadable file cannot be imported as the
 * first option in a list by pressing Enter.
 *
 * @param {{kind: string, reason: string}|null} detected  classifyGltf's verdict
 * @returns {{options: object[], suggested: string|null, detected: object|null, summary: string}}
 */
export function gltfImportChoice(detected) {
  const suggested = detected ? wizardKeyForKind(detected.kind) : null
  return {
    options: GLTF_IMPORT_TYPES,
    suggested,
    detected: detected ?? null,
    summary: describeDetection(detected),
  }
}
